import { Auth } from ".";
import { clearProfile } from "actions/profile";
import { clearSites } from "actions/site";
import { setLastRefreshTime } from "utils/indexDbStorage";
import {
  removeSession,
  getClearStorageTime,
  removeClearStorage,
} from "utils/localStorage";

export const LOGIN_REQUEST = "@@session/LOGIN_REQUEST";
export const LOGIN_SUCCESS = "@@session/LOGIN_SUCCESS";
export const LOGIN_FAILURE = "@@session/LOGIN_FAILURE";

export function login(formData) {
  return async (dispatch) => {
    dispatch(clearProfile());
    dispatch(clearSites());
    dispatch({
      type: LOGIN_REQUEST,
      error: {},
    });

    try {
      const response = await Auth.signIn(
        formData.email,
        formData.password?.trim()
      );
      dispatch({
        type: LOGIN_SUCCESS,
        payload: response,
      });
      setLastRefreshTime(new Date().getTime());
    } catch (error) {
      dispatch({
        type: LOGIN_FAILURE,
        payload: error,
      });
    }
  };
}

export const CHANGE_PASSWORD_REQUEST = "@@session/CHANGE_PASSWORD_REQUEST";
export const CHANGE_PASSWORD_SUCCESS = "@@session/CHANGE_PASSWORD_SUCCESS";
export const CHANGE_PASSWORD_FAILURE = "@@session/CHANGE_PASSWORD_FAILURE";

export function changePassword(user, formData) {
  return async (dispatch) => {
    dispatch({
      type: CHANGE_PASSWORD_REQUEST,
    });

    try {
      const response = await Auth.changePassword(
        user,
        formData?.oldPassword,
        formData?.newPassword
      );
      dispatch({
        type: CHANGE_PASSWORD_SUCCESS,
        payload: response,
      });
    } catch (error) {
      dispatch({
        type: CHANGE_PASSWORD_FAILURE,
        payload: error,
      });
    }
  };
}

export const UPDATE_DETAILS_REQUEST = "@@session/UPDATE_DETAILS_REQUEST";
export const UPDATE_DETAILS_SUCCESS = "@@session/UPDATE_DETAILS_SUCCESS";
export const UPDATE_DETAILS_FAILURE = "@@session/UPDATE_DETAILS_FAILURE";

export function updateDetails(user, password, data) {
  return async (dispatch) => {
    dispatch({
      type: UPDATE_DETAILS_REQUEST,
    });
    try {
      const response = await Auth.completeNewPassword(
        user,
        password.trim(),
        data
      );
      dispatch({
        type: UPDATE_DETAILS_SUCCESS,
        payload: response,
      });
    } catch (error) {
      dispatch({
        type: UPDATE_DETAILS_FAILURE,
        payload: error,
      });
    }
  };
}

export const CHECK_SESSION_REQUEST = "@@session/CHECK_SESSION_REQUEST";
export const CHECK_SESSION_SUCCESS = "@@session/CHECK_SESSION_SUCCESS";
export const CHECK_SESSION_FAILURE = "@@session/CHECK_SESSION_FAILURE";

export function checkSession() {
  return async (dispatch) => {
    dispatch({
      type: CHECK_SESSION_REQUEST,
    });

    try {
      const response = await Auth.currentAuthenticatedUser();
      const currentUnixTime = Math.round(new Date().getTime() / 1000);
      const exp = response?.signInUserSession?.idToken?.payload?.exp;
      if (exp - currentUnixTime >= 0) {
        dispatch({
          type: CHECK_SESSION_SUCCESS,
          payload: response,
        });
      } else {
        dispatch({
          type: CHECK_SESSION_FAILURE,
        });
      }
    } catch (error) {
      dispatch({
        type: CHECK_SESSION_FAILURE,
      });
    }
  };
}

export const LOGOUT_REQUEST = "@@session/LOGOUT_REQUEST";
export const LOGOUT_SUCCESS = "@@session/LOGOUT_SUCCESS";
export const LOGOUT_FAILURE = "@@session/LOGOUT_FAILURE";

export function logout() {
  return async (dispatch) => {
    dispatch({
      type: LOGOUT_REQUEST,
    });
    removeSession();
    try {
      await Auth.signOut();
      dispatch({
        type: LOGOUT_SUCCESS,
      });
      dispatch(clearProfile());
      dispatch(clearSites());
    } catch (error) {
      dispatch({
        type: LOGOUT_FAILURE,
        payload: error,
      });
    }
  };
}

export const SESSION_REFRESH_REQUEST = "@@session/SESSION_REFRESH_REQUEST";
export const SESSION_REFRESH_SUCCESS = "@@session/SESSION_REFRESH_SUCCESS";
export const SESSION_REFRESH_FAILURE = "@@session/SESSION_REFRESH_FAILURE";

export function sessionRefresh() {
  return async (dispatch) => {
    dispatch({
      type: SESSION_REFRESH_REQUEST,
    });
    try {
      const cognitoUser = await Auth.currentAuthenticatedUser();
      const currentSession = await Auth.currentSession();
      cognitoUser.refreshSession(
        currentSession.refreshToken,
        (err, session) => {
          dispatch({
            type: SESSION_REFRESH_SUCCESS,
            payload: session,
          });
        }
      );
    } catch (error) {
      dispatch({
        type: SESSION_REFRESH_FAILURE,
      });
    }
  };
}

export const FORGOT_PASSWORD_REQUEST = "@@session/FORGOT_PASSWORD_REQUEST";
export const FORGOT_PASSWORD_SUCCESS = "@@session/FORGOT_PASSWORD_SUCCESS";
export const FORGOT_PASSWORD_FAILURE = "@@session/FORGOT_PASSWORD_FAILURE";

export function forgotPassword(username) {
  return async (dispatch) => {
    dispatch({
      type: FORGOT_PASSWORD_REQUEST,
    });
    try {
      const response = await Auth.forgotPassword(username);
      dispatch({
        type: FORGOT_PASSWORD_SUCCESS,
        payload: response,
      });
    } catch (error) {
      dispatch({
        type: FORGOT_PASSWORD_FAILURE,
        payload: error,
      });
    }
  };
}

export const FORGOT_PASSWORD_SUBMIT_REQUEST =
  "@@session/FORGOT_PASSWORD_SUBMIT_REQUEST";
export const FORGOT_PASSWORD_SUBMIT_SUCCESS =
  "@@session/FORGOT_PASSWORD_SUBMIT_SUCCESS";
export const FORGOT_PASSWORD_SUBMIT_FAILURE =
  "@@session/FORGOT_PASSWORD_SUBMIT_FAILURE";

export function forgotPasswordSubmit(formData) {
  return async (dispatch) => {
    dispatch({
      type: FORGOT_PASSWORD_SUBMIT_REQUEST,
    });
    try {
      const response = await Auth.forgotPasswordSubmit(
        formData.email,
        formData.code,
        formData.password
      );
      dispatch({
        type: FORGOT_PASSWORD_SUBMIT_SUCCESS,
        payload: response,
      });
      dispatch(
        login({
          email: formData.email,
          password: formData.password,
        })
      );
    } catch (error) {
      dispatch({
        type: FORGOT_PASSWORD_SUBMIT_FAILURE,
        payload: error,
      });
    }
  };
}

export const RESET_FORGOT_PASSWORD_FLAG =
  "@@session/RESET_FORGOT_PASSWORD_FLAG";

export function resetForgotPasswordFlag() {
  return async (dispatch) => {
    dispatch({
      type: RESET_FORGOT_PASSWORD_FLAG,
    });
  };
}

export const CLEAR_SESSION_ERROR = "@@session/CLEAR_SESSION_ERROR";
export function clearSessionError() {
  return async (dispatch) => {
    dispatch({
      type: CLEAR_SESSION_ERROR,
    });
  };
}

export function clearAllSessionData() {
  return async (dispatch) => {
    removeSession();
    dispatch(clearProfile());
    dispatch(clearSites());
    dispatch({
      type: LOGOUT_SUCCESS,
    });
  };
}

export function checkStorage() {
  return async (dispatch) => {
    const time = getClearStorageTime();
    if (!time) {
      removeClearStorage();
      dispatch(clearAllSessionData());
    }
  };
}
